/* Dýravinir UX-160: conversion helpers built on verified storefront and policy truth. */
(() => {
  'use strict';

  const STORAGE = {
    recent: 'dyravinir:ux160:recent',
    removed: 'dyravinir:ux160:removed-cart-line'
  };
  const clean = (value) => (value || '').replace(/\s+/g, ' ').trim();
  const lower = (value) => clean(value).toLocaleLowerCase('is');
  const family = (value) => lower(value).replace(/\b\d+(?:[.,]\d+)?\s*(?:kg|g|gr\.?)\b/g, '').replace(/\s*[-–]\s*$/, '').trim();

  function read(key, fallback) {
    try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch (_) { return fallback; }
  }

  function write(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
  }

  function analyticsAllowed() {
    try { return Boolean(window.Shopify?.customerPrivacy?.analyticsProcessingAllowed()); } catch (_) { return false; }
  }

  function track(event, payload = {}) {
    if (!analyticsAllowed()) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, release: 'ux160-20260905', ...payload });
  }

  function integrateBackInStock(root = document) {
    const recovery = root.querySelector('[data-dv140-unavailable]') || document.querySelector('[data-dv140-unavailable]');
    const appButton = document.querySelector('#BIS_trigger');
    if (!recovery || !appButton) return;
    const actions = recovery.querySelector('.dv140-unavailable__actions');
    if (!actions || actions.contains(appButton)) return;
    appButton.textContent = 'Láta mig vita þegar varan fæst';
    appButton.classList.add('dv160-back-in-stock');
    appButton.removeAttribute('style');
    appButton.addEventListener('click', () => track('dv_back_in_stock_open', { path: location.pathname }), { once: true });
    actions.prepend(appButton);
  }

  function watchBackInStock() {
    integrateBackInStock();
    const observer = new MutationObserver(() => integrateBackInStock());
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 15000);
  }

  function addListingReturn(root = document) {
    if (!document.body.classList.contains('template-product')) return;
    const host = root.querySelector('[data-dv160-listing-return]') || document.querySelector('[data-dv160-listing-return]');
    if (!host || !document.referrer) return;
    let previous;
    try { previous = new URL(document.referrer); } catch (_) { return; }
    if (previous.origin !== location.origin || !/^\/(collections|search)/.test(previous.pathname)) return;
    host.href = `${previous.pathname}${previous.search}`;
    host.textContent = previous.pathname === '/search' ? 'Til baka í leitarniðurstöður' : 'Til baka í vörulistann';
    host.hidden = false;
  }

  function renderRecent(root = document) {
    const host = root.querySelector('[data-dv160-recent]') || document.querySelector('[data-dv160-recent]');
    if (!host) return;
    const current = window.dv160Product;
    let items = read(STORAGE.recent, []).filter((item) => item && item.url && item.title);
    if (current?.url) {
      items = items.filter((item) => item.url !== current.url);
      items.unshift(current);
      items = items.slice(0, 12);
      write(STORAGE.recent, items);
    }
    const sameSpecies = items.filter((item) => !current?.species || item.species === current.species).slice(current ? 1 : 0, current ? 5 : 4);
    if (!sameSpecies.length) { host.hidden = true; return; }
    const list = host.querySelector('[data-dv160-recent-list]');
    if (!list) return;
    list.replaceChildren(...sameSpecies.map((item) => {
      const li = document.createElement('li');
      const link = document.createElement('a');
      link.href = item.url;
      link.className = 'dv160-recent__card';
      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = '';
        image.loading = 'lazy';
        image.width = 180;
        image.height = 180;
        link.appendChild(image);
      }
      const text = document.createElement('span');
      text.innerHTML = `<strong></strong><small></small>`;
      text.querySelector('strong').textContent = item.title;
      text.querySelector('small').textContent = 'Skoða verð og pakkningar';
      link.appendChild(text);
      li.appendChild(link);
      return li;
    }));
    host.hidden = false;
  }

  function scoreRecommendation(card, current) {
    let score = 0;
    if (card.dataset.productAvailable === 'true') score += 50;
    if (lower(card.dataset.productSpecies) === lower(current.species)) score += 20;
    if (lower(card.dataset.productVendor) === lower(current.vendor)) score += 10;
    if (lower(card.dataset.productType) === lower(current.type)) score += 8;
    if (family(card.dataset.productTitle) === family(current.title)) score += 30;
    return score;
  }

  function rankRecommendations(root = document) {
    const current = window.dv160Product;
    if (!current) return;
    root.querySelectorAll('product-recommendations ul.product-grid').forEach((grid) => {
      if (grid.dataset.dv160Ranked === 'true') return;
      const rows = Array.from(grid.children);
      rows.sort((a, b) => scoreRecommendation(b.querySelector('[data-dv160-card]') || {}, current)
        - scoreRecommendation(a.querySelector('[data-dv160-card]') || {}, current));
      rows.forEach((row) => grid.appendChild(row));
      grid.dataset.dv160Ranked = 'true';
    });
  }

  function showUndoToast(item) {
    if (!item?.variantId || Date.now() - item.at > 30000 || document.querySelector('[data-dv160-undo]')) return;
    const toast = document.createElement('div');
    toast.className = 'dv160-undo';
    toast.dataset.dv160Undo = 'true';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    const label = document.createElement('span');
    label.textContent = `${item.title || 'Vara'} fjarlægð úr körfu.`;
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'Afturkalla';
    button.addEventListener('click', async () => {
      button.disabled = true;
      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(item.variantId), quantity: Number(item.quantity) || 1 }] })
        });
        if (!response.ok) throw new Error('cart add failed');
        localStorage.removeItem(STORAGE.removed);
        track('dv_cart_remove_undo');
        location.reload();
      } catch (_) {
        label.textContent = 'Ekki tókst að setja vöruna aftur í körfu. Reyndu aftur.';
        button.disabled = false;
      }
    });
    toast.append(label, button);
    document.body.appendChild(toast);
    window.setTimeout(() => toast.remove(), 12000);
  }

  function setupCartUndo() {
    const saved = read(STORAGE.removed, null);
    if (saved && Date.now() - saved.at <= 30000) showUndoToast(saved);
    else if (saved) localStorage.removeItem(STORAGE.removed);
    document.addEventListener('click', (event) => {
      const remove = event.target.closest('cart-remove-button');
      if (!remove) return;
      const row = remove.closest('.cart-item');
      const quantity = row?.querySelector('input[name="updates[]"]');
      const item = {
        variantId: quantity?.dataset.quantityVariantId,
        quantity: Number(quantity?.value) || 1,
        title: clean(row?.querySelector('.cart-item__name')?.textContent),
        at: Date.now()
      };
      if (!item.variantId) return;
      write(STORAGE.removed, item);
      window.setTimeout(() => showUndoToast(item), 900);
      track('dv_cart_remove', { undo_available: true });
    }, true);
  }

  function labelAcceleratedCheckout(root = document) {
    root.querySelectorAll('.additional-checkout-buttons').forEach((box) => {
      if (box.previousElementSibling?.classList.contains('dv160-accelerated-label')) return;
      const label = document.createElement('p');
      label.className = 'dv160-accelerated-label';
      label.textContent = 'Eða greiða hraðar';
      box.before(label);
    });
  }

  function normalizeSearchForms(root = document) {
    const exact = new Map([
      ['hundafodur', 'hundafóður'], ['kattafodur', 'kattafóður'], ['kjuklingur', 'kjúklingur'],
      ['hrossakjot', 'hrossakjöt'], ['visundakjot', 'vísundakjöt'], ['dadyr', 'dádýr'],
      ['kornlaus', 'kornlaust'], ['grainfree', 'kornlaust']
    ]);
    root.querySelectorAll('form[action*="/search"]').forEach((form) => {
      if (form.dataset.dv160Search === 'true') return;
      form.dataset.dv160Search = 'true';
      form.addEventListener('submit', () => {
        const input = form.querySelector('input[name="q"]');
        const replacement = exact.get(lower(input?.value));
        if (replacement) input.value = replacement;
      });
    });
  }

  function accessibilityPass(root = document) {
    root.querySelectorAll('[data-dv160-recent-list]').forEach((item) => item.setAttribute('aria-live', 'polite'));
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const tray = document.querySelector('.dv140-compare-tray:not([hidden])');
      if (tray) tray.querySelector('.dv140-compare-tray__close')?.click();
      document.querySelector('[data-dv160-undo]')?.remove();
    });
  }

  function init(root = document) {
    watchBackInStock();
    addListingReturn(root);
    renderRecent(root);
    rankRecommendations(root);
    setupCartUndo();
    labelAcceleratedCheckout(root);
    normalizeSearchForms(root);
    accessibilityPass(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  new MutationObserver(() => rankRecommendations()).observe(document.documentElement, { childList: true, subtree: true });
})();
